import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  TextInput,
  Layer,
  Spinner,
  Select,
} from "grommet";
import { Info, Search, Add } from "grommet-icons";
import { useNavigate, useParams } from "react-router-dom";
import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import LearningPathTable from "../../components/AdminPortal/LearningPathTable";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import AddLearningPathModal from "../../components/AdminPortal/AddLearningPathModal";

const ManageLearningPaths = () => {
  const navigate = useNavigate();
  const { portfolio_id } = useParams();

  const [learningPaths, setLearningPaths] = useState([]);
  const [portfolioName, setPortfolioName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [coursesList, setCoursesList] = useState([]);

  // pull fetchLearningPaths into a stable function
  const fetchLearningPaths = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/learning-paths/${portfolio_id}`);
      const data = await response.json();
      setLearningPaths(data.learning_paths || []);
    } catch (err) {
      setError(err.message || "Error fetching learning paths");
    } finally {
      setLoading(false);
    }
  }, [portfolio_id]);

  // Fetch portfolio name
  useEffect(() => {
    const fetchPortfolioName = async () => {
      try {
        const response = await fetch(`/api/admin/portfolio/${portfolio_id}`);
        const data = await response.json();
        setPortfolioName(data.portfolio_name);
      } catch (err) {
        console.error("Error fetching portfolio details:", err);
      }
    };
    if (portfolio_id) fetchPortfolioName();
  }, [portfolio_id]);

  // Fetch learning paths
  useEffect(() => {
    if (portfolio_id) fetchLearningPaths();
  }, [portfolio_id, fetchLearningPaths]);

  // Fetch courses when opening modal
  useEffect(() => {
    if (showAddModal) {
      const fetchCourses = async () => {
        try {
          const response = await fetch("/api/admin/learning-path/courses", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              learningPathIds: learningPaths.map((lp) => lp.learning_path_id),
            }),
          });
          const data = await response.json();
          setCoursesList(data.courses || []);
        } catch (err) {
          console.error("Error fetching courses:", err);
        }
      };
      fetchCourses();
    }
  }, [showAddModal, learningPaths]);

  const filteredPaths = learningPaths.filter((item) =>
    item.learning_path_name.toLowerCase().includes(searchValue.toLowerCase()),
  );

  if (loading) {
    return (
      <Box fill align="center" justify="center" background="light-2">
        <Spinner size="large" />
        <Text>Loading learning paths...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box fill align="center" justify="center" background="light-2">
        <Text color="status-critical">{error}</Text>
      </Box>
    );
  }

  return (
    <Box fill="vertical" background="light-2">
      <HeaderNavigation />
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: "Actions", link: `/admin/portfolio/${portfolio_id}` },
          {
            label: "Manage Learning Paths",
            link: `/admin/portfolio/${portfolio_id}/learning-paths`,
          },
        ]}
      />
      <Box height="2px" background="brand" margin="small" />

      <Box flex pad="small">
        <Box
          background="#F2F2F2"
          elevation="small"
          round="small"
          pad="medium"
          gap="medium"
        >
          <Box direction="row" align="center" gap="small">
            <Info size="medium" color="brand" />
            <Heading level="3" margin="none" color="brand">
              Manage Learning Paths
            </Heading>
          </Box>
          <Text>
            This page allows you to manage all learning paths within the
            portfolio—view each path&apos;s publish status, track the number of
            courses by type (Prerequisite, Foundation, Intermediate), explore or
            modify courses in a path, reorder them, edit or archive the learning
            path, and also add new learning paths as needed.
          </Text>

          <Box
            direction="row"
            justify="between"
            align="center"
            margin={{ top: "small" }}
          >
            <Box>
              <Heading level="3" margin="none">
                {portfolioName} - Manage Learning Paths
              </Heading>
              <Text>
                {filteredPaths.length} learning paths have been added.
              </Text>
            </Box>
            <Box direction="row" gap="small" align="center">
              <TextInput
                icon={<Search />}
                placeholder="Search learning path"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                margin={{ right: "small" }}
                width="300px"
              />
              <Button
                icon={<Add />}
                label="Add Learning Path"
                secondary
                onClick={() => setShowAddModal(true)}
                style={{ whiteSpace: "nowrap" }}
              />
            </Box>
          </Box>

          <Box round="small" overflow="auto">
            <LearningPathTable
              skillData={filteredPaths}
              portfolio_id={portfolio_id}
              onEdit={(updated) => {
                setLearningPaths((prev) =>
                  prev.map((lp) =>
                    lp.learning_path_id === updated.learning_path_id
                      ? updated
                      : lp,
                  ),
                );
              }}
            />
          </Box>
        </Box>
      </Box>

      <StickyFooter />

      {showAddModal && (
        <AddLearningPathModal
          portfolio_id={portfolio_id}
          onClose={() => setShowAddModal(false)}
          onCreated={fetchLearningPaths}
        />
      )}
    </Box>
  );
};

export default ManageLearningPaths;
